import React, {useState, ReactElement} from 'react'
import {NavLink} from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select, {SelectChangeEvent} from '@mui/material/Select'
import Switch from '@mui/material/Switch'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {ConfirmationDialog} from '../../components/dialogs'
import {VisuallyHiddenInput, roundedButton, TextVariant, Text} from '../../components/text'

export type InputProps = {
  label?: string
  value?: string | undefined
  defaultValue?: string | undefined
  setter?: (v: string) => void
  validate?: boolean
  validator?: (v: string | undefined) => string | boolean
  type?: string
  multiline?: boolean
  sx?: any
  inputProps?: {[key: string]: any}
}
export const Input = ({
  label,
  value,
  defaultValue,
  setter,
  validate,
  validator,
  type,
  inputProps,
  multiline,
  sx,
}: InputProps) => {
  const error = validate && validator && validator(value)
  return (
    <TextField
      label={label}
      helperText={error}
      error={!!error}
      value={value}
      defaultValue={defaultValue}
      onChange={(e) => setter && setter(e.target.value)}
      type={type}
      multiline={multiline}
      inputProps={inputProps}
      sx={sx}
    />
  )
}

type InputFieldUploadType = {
  text?: string
  sx?: {[k: string]: any}
  onUpload: (data: any) => void
}

export const InputFileUpload = ({onUpload, text, sx}: InputFieldUploadType) => {
  return (
    <Button
      component="label"
      variant="contained"
      startIcon={<CloudUploadIcon />}
      sx={{...roundedButton, ...sx}}
    >
      {text || 'Upload file'}
      <VisuallyHiddenInput
        type="file"
        onChange={(e) => onUpload(e.target?.files && e.target.files[0])}
      />
    </Button>
  )
}

type EditableTextType = {
  value: string | undefined
  variant?: TextVariant
  onChange?: (val: any) => void | Promise<any>
  multiline?: boolean
  markdown?: boolean
  textSx?: any
  editSx?: any
}
export const EditableText = ({
  value,
  variant,
  multiline,
  markdown,
  onChange,
  editSx,
  textSx,
}: EditableTextType) => {
  const [currentValue, setCurrentValue] = useState(value)
  const [isEdit, setIsEdit] = useState(false)

  return (
    <Box>
      {isEdit ? (
        <Stack direction="row">
          <Input value={currentValue} setter={setCurrentValue} sx={editSx} multiline={multiline} />
          <Button
            variant="text"
            onClick={() => {
              onChange && onChange(currentValue)
              setIsEdit(!isEdit)
            }}
          >
            Update
          </Button>
        </Stack>
      ) : (
        <Stack direction="row">
          <Text variant={variant} style={textSx} text={currentValue || ''} markdown={markdown} />
          <Button variant="text" onClick={() => setIsEdit(!isEdit)}>
            Edit
          </Button>
        </Stack>
      )}
    </Box>
  )
}

// TODO: This can probably be removed?
type EditToggleType = {
  title: string
  value: string | undefined
  on?: boolean
  onChange?: (v: {on: boolean; value: string | undefined}) => void
}
export const EditToggle = ({title, value, on, onChange}: EditToggleType) => {
  const [switchOn, setSwitchOn] = useState(on)
  const [currentValue, setCurrentValue] = useState(value)
  const [isEdit, setIsEdit] = useState(false)

  const notifyChange = (on: boolean, value: string | undefined) => {
    onChange && onChange({on, value})
  }
  return (
    <Box>
      <Switch
        checked={switchOn}
        onChange={(e) => {
          setSwitchOn(e.target.checked)
          notifyChange(e.target.checked, currentValue)
        }}
      />
      {isEdit ? (
        <>
          <Input value={currentValue} setter={setCurrentValue} />
          <Button
            variant="text"
            onClick={() => {
              setIsEdit(!isEdit)
              notifyChange(!!switchOn, currentValue)
            }}
          >
            Update
          </Button>
        </>
      ) : (
        <>
          <span>
            {title}: {currentValue}
          </span>
          <Button variant="text" onClick={() => setIsEdit(!isEdit)}>
            Edit
          </Button>
        </>
      )}
    </Box>
  )
}

type ToggleType = {
  title: string
  on?: boolean
  onChange?: (v: boolean) => void
}
export const Toggle = ({title, on, onChange}: ToggleType) => {
  const [switchOn, setSwitchOn] = useState(on)

  return (
    <Box>
      <Switch
        checked={switchOn || false}
        onChange={(e) => {
          setSwitchOn(e.target.checked)
          onChange && onChange(e.target.checked)
        }}
      />
      <span>{title}</span>
    </Box>
  )
}

type SelectProps = {
  value?: string | string[]
  label?: string
  defaultValue?: any
  variant?: string
  values: {[key: string]: string}
  onChange?: (v: any) => void
  multiple?: boolean
  validate?: boolean
  validator?: (v: any) => string | boolean
  sx?: any
  alwaysSelected?: boolean
  unselected?: string
}
export const SelectElem = ({
  label,
  values,
  variant,
  value,
  defaultValue,
  multiple,
  validate,
  validator,
  onChange,
  alwaysSelected,
  unselected = '-',
  sx,
}: SelectProps) => {
  const [val, setValue] = useState(defaultValue || '')
  const labelId = (label || '').toLowerCase().replace(' ', '-')

  const handleChange = (e: SelectChangeEvent) => {
    const val = e.target.value
    setValue(val)
    onChange && onChange(val)
  }

  const error = validate && validator && validator(val)

  return (
    <FormControl sx={{minWidth: 250, ...sx}} variant={variant as any} error={!!error}>
      {label && <InputLabel id={labelId}>{error || label}</InputLabel>}
      <Select
        labelId={labelId}
        value={value as any}
        defaultValue={defaultValue}
        label={values[value || val]}
        multiple={multiple || undefined}
        onChange={handleChange}
      >
        {!alwaysSelected && (
          <MenuItem value="">
            <em>{unselected}</em>
          </MenuItem>
        )}
        {Object.entries(values).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

type RoundedButtonProps = {
  label?: string | ReactElement
  disabled?: boolean
  onClick?: () => void
  type?: string
  loading?: boolean
  endIcon?: any
  sx?: {[key: string]: any}
}
export const RoundedButton = ({
  label,
  disabled,
  type,
  loading,
  endIcon,
  onClick,
  sx,
}: RoundedButtonProps) => (
  <Button
    variant="contained"
    disabled={disabled || loading}
    sx={{...roundedButton, ...sx}}
    type={type as any}
    onClick={onClick}
    endIcon={endIcon}
  >
    {loading ? <CircularProgress color="secondary" size={25} /> : label}
  </Button>
)

export const NavButton = ({
  label,
  to,
  sx,
}: {
  label?: string | ReactElement
  to: string
  sx?: {[k: string]: any}
}) => (
  <Button variant="contained" component={NavLink} to={to} sx={{...roundedButton, ...sx}}>
    {label}
  </Button>
)

type ActionButtonProps = RoundedButtonProps & {
  action: () => Promise<any>
  confirmationText?: string
  confirmationLabel?: string
}
export const ActionButton = (props: ActionButtonProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showLoading, setLoading] = useState(false)

  const executeAction = async () => {
    setLoading(true)
    try {
      await props.action()
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  const handleDialog = (confirmed: boolean) => {
    if (confirmed) {
      executeAction()
    }
    setShowConfirmation(false)
  }

  const handleClick = async () => {
    if (confirmationText) {
      setShowConfirmation(true)
    } else {
      return executeAction()
    }
  }

  const {loading, confirmationText, confirmationLabel} = props
  const actionProps = ['action', 'loading', 'confirmationText', 'confirmationLabel']
  const buttonProps = Object.fromEntries(
    Object.entries(props).filter(([k, v]) => !actionProps.includes(k))
  )
  return (
    <>
      <ConfirmationDialog
        label={confirmationLabel || ''}
        text={confirmationText || ''}
        onSelect={handleDialog}
        open={showConfirmation}
      />
      <RoundedButton {...buttonProps} onClick={handleClick} loading={showLoading || loading} />
    </>
  )
}

export const RedirectButton = ({
  label,
  disabled,
  urlGetter,
  sx,
}: {
  label?: string | ReactElement
  disabled?: boolean
  urlGetter: () => Promise<string>
  sx?: {[k: string]: any}
}) => (
  <ActionButton
    label={label}
    disabled={disabled}
    sx={sx}
    action={async () => {
      const url = await urlGetter()
      if (url) {
        window.location.href = url
      }
    }}
  />
)

export const DownloadButton = (props: RoundedButtonProps & {fetcher: () => Promise<any>}) => {
  const [error, setError] = useState('')
  const [showLoading, setLoading] = useState(false)

  const handleDownload = async () => {
    try {
      setError('')
      setLoading(true)
      const blob = await props.fetcher()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', 'tasks.csv')
      document.body.appendChild(link)
      link.click()
      link?.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading file:', error)
      setError('Error downloading file.')
    }
    setLoading(false)
  }

  return (
    <>
      <RoundedButton {...props} onClick={handleDownload} loading={showLoading || props.loading} />
      {error && <Alert severity="error">{error}</Alert>}
    </>
  )
}
